import React, { useState, useEffect, useRef } from 'react';
import { mapsAPI } from '../../services/api';
import {
  X, MapPin, Clock, Eye, EyeOff, Zap, ZapOff,
  Link, Copy, Check, Calendar, BarChart2, Hash,
  RefreshCw, AlertCircle
} from 'lucide-react';
import './EditMapModal.css';

const EditMapModal = ({ map, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startTime: '',
    endTime: '',
    isActive: false,
    isPublic: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const titleRef = useRef(null);

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  useEffect(() => {
    if (map && isOpen) {
      setFormData({
        title: map.title || '',
        description: map.description || '',
        startTime: formatDateForInput(map.startTime),
        endTime: formatDateForInput(map.endTime),
        isActive: map.isActive || false,
        isPublic: map.isPublic || false
      });
      setError(null);
      setTimeout(() => titleRef.current?.focus(), 100);
    }
  }, [map, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const start = new Date(formData.startTime);
    const end = new Date(formData.endTime);

    if (end <= start) {
      setError('End time must be after start time');
      setLoading(false);
      return;
    }

    try {
      const updateData = {
        title: formData.title,
        description: formData.description,
        startTime: start.toISOString(),
        endTime: end.toISOString(),
        isActive: formData.isActive,
        isPublic: formData.isPublic
      };

      const response = await mapsAPI.updateMap(map.id, updateData);
      onSave(response.data.data.map);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update map');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleClose = () => {
    setFormData({ title: '', description: '', startTime: '', endTime: '', isActive: false, isPublic: false });
    setError(null);
    onClose();
  };

  const copyShareLink = () => {
    if (map?.shareUrl) {
      navigator.clipboard.writeText(map.shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  if (!isOpen) return null;

  return (
    <div className="emm-overlay" onClick={handleOverlayClick}>
      <div className="emm-modal">

        {/* Header */}
        <div className="emm-header">
          <div className="emm-header-left">
            <div className="emm-header-icon">
              <MapPin size={16} />
            </div>
            <div>
              <p className="emm-header-eyebrow">Edit Map</p>
              <h2 className="emm-header-title">{map?.title || 'Untitled Map'}</h2>
            </div>
          </div>
          <button className="emm-close" onClick={handleClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="emm-form">

          {/* Title */}
          <div className="emm-field">
            <label className="emm-label" htmlFor="emm-title">
              Map Title <span className="emm-required">*</span>
            </label>
            <input
              ref={titleRef}
              id="emm-title"
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              maxLength={100}
              className="emm-input"
              placeholder="e.g. Friday Night Market — May 2025"
            />
            <span className="emm-hint">{formData.title.length}/100</span>
          </div>

          {/* Description */}
          <div className="emm-field">
            <label className="emm-label" htmlFor="emm-desc">Description</label>
            <textarea
              id="emm-desc"
              name="description"
              value={formData.description}
              onChange={handleChange}
              maxLength={500}
              rows={3}
              className="emm-textarea"
              placeholder="Optional — describe this map for your attendees"
            />
            <span className="emm-hint">{formData.description.length}/500</span>
          </div>

          {/* Date Range */}
          <div className="emm-row">
            <div className="emm-field">
              <label className="emm-label" htmlFor="emm-start">
                <Calendar size={13} className="emm-label-icon" />
                Start Time <span className="emm-required">*</span>
              </label>
              <input
                id="emm-start"
                type="datetime-local"
                name="startTime"
                value={formData.startTime}
                onChange={handleChange}
                required
                className="emm-input"
              />
            </div>
            <div className="emm-field">
              <label className="emm-label" htmlFor="emm-end">
                <Calendar size={13} className="emm-label-icon" />
                End Time <span className="emm-required">*</span>
              </label>
              <input
                id="emm-end"
                type="datetime-local"
                name="endTime"
                value={formData.endTime}
                onChange={handleChange}
                required
                className="emm-input"
              />
            </div>
          </div>

          {/* Toggles */}
          <div className="emm-toggles">
            <div className={`emm-toggle-card ${formData.isActive ? 'emm-toggle-card--on' : ''}`}>
              <div className="emm-toggle-info">
                <div className="emm-toggle-icon-wrap emm-toggle-icon-active">
                  {formData.isActive ? <Zap size={15} /> : <ZapOff size={15} />}
                </div>
                <div>
                  <p className="emm-toggle-label">Map Active</p>
                  <p className="emm-toggle-sub">
                    {formData.isActive ? 'Attendees can access this map' : 'Map is currently disabled'}
                  </p>
                </div>
              </div>
              <label className="emm-switch">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                />
                <span className="emm-switch-track">
                  <span className="emm-switch-thumb" />
                </span>
              </label>
            </div>

            <div className={`emm-toggle-card ${formData.isPublic ? 'emm-toggle-card--on' : ''}`}>
              <div className="emm-toggle-info">
                <div className="emm-toggle-icon-wrap emm-toggle-icon-public">
                  {formData.isPublic ? <Eye size={15} /> : <EyeOff size={15} />}
                </div>
                <div>
                  <p className="emm-toggle-label">Public Access</p>
                  <p className="emm-toggle-sub">
                    {formData.isPublic ? 'Anyone with the link can view' : 'Only you can view this map'}
                  </p>
                </div>
              </div>
              <label className="emm-switch">
                <input
                  type="checkbox"
                  name="isPublic"
                  checked={formData.isPublic}
                  onChange={handleChange}
                />
                <span className="emm-switch-track">
                  <span className="emm-switch-thumb" />
                </span>
              </label>
            </div>
          </div>

          {/* Map Info */}
          <div className="emm-info-block">
            <p className="emm-info-title">Map Details</p>
            <div className="emm-info-grid">
              <div className="emm-info-item">
                <span className="emm-info-icon"><Hash size={12} /></span>
                <span className="emm-info-key">Map ID</span>
                <span className="emm-info-val emm-mono">{map?.mapId || '—'}</span>
              </div>
              <div className="emm-info-item">
                <span className="emm-info-icon"><Clock size={12} /></span>
                <span className="emm-info-key">Created</span>
                <span className="emm-info-val">
                  {map?.createdAt ? new Date(map.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                </span>
              </div>
              <div className="emm-info-item">
                <span className="emm-info-icon"><RefreshCw size={12} /></span>
                <span className="emm-info-key">Updated</span>
                <span className="emm-info-val">
                  {map?.updatedAt ? new Date(map.updatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                </span>
              </div>
              <div className="emm-info-item">
                <span className="emm-info-icon"><Eye size={12} /></span>
                <span className="emm-info-key">Visits</span>
                <span className="emm-info-val">{map?.visitCount ?? 0}</span>
              </div>
              <div className="emm-info-item">
                <span className="emm-info-icon"><MapPin size={12} /></span>
                <span className="emm-info-key">Markers</span>
                <span className="emm-info-val">{map?.markersCount ?? 0}</span>
              </div>
              <div className="emm-info-item">
                <span className="emm-info-icon"><BarChart2 size={12} /></span>
                <span className="emm-info-key">Status</span>
                <span className={`emm-status-pill ${formData.isActive ? 'emm-status-pill--active' : 'emm-status-pill--inactive'}`}>
                  {formData.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          {/* Share Link */}
          {map?.shareUrl && (
            <div className="emm-field">
              <label className="emm-label">
                <Link size={13} className="emm-label-icon" />
                Share Link
              </label>
              <div className="emm-share-row">
                <input
                  type="text"
                  value={map.shareUrl}
                  readOnly
                  className="emm-input emm-share-input emm-mono"
                  onClick={(e) => e.target.select()}
                />
                <button type="button" onClick={copyShareLink} className={`emm-copy-btn ${copied ? 'emm-copy-btn--done' : ''}`}>
                  {copied ? <Check size={15} /> : <Copy size={15} />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="emm-error">
              <AlertCircle size={15} />
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="emm-actions">
            <button type="button" onClick={handleClose} className="emm-btn emm-btn--ghost" disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="emm-btn emm-btn--primary" disabled={loading}>
              {loading ? (
                <>
                  <span className="emm-spinner" />
                  Saving…
                </>
              ) : 'Save Changes'}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default EditMapModal;